(() => {
  if (customElements.get("matching-variant-gift")) return;

  const normalize = (value) => String(value || "")
    .trim()
    .toLocaleLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "");

  class MatchingVariantGift extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.image = this.querySelector(".matching-gift__image");
      this.optionValue = this.querySelector("[data-gift-option-value]");
      this.comparePrice = this.querySelector("[data-gift-compare-price]");
      this.status = this.querySelector("[data-gift-status]");

      try {
        this.mainProduct = JSON.parse(this.querySelector("[data-main-product]").textContent);
        this.giftProduct = JSON.parse(this.querySelector("[data-gift-product]").textContent);
      } catch (error) {
        console.warn("No se han podido leer los datos del producto de regalo.", error);
        this.hidden = false;
        if (this.status) this.status.textContent = "No se han podido cargar los datos del regalo.";
        return;
      }

      this.form = document.getElementById(this.dataset.formId);
      this.formRetryCount = 0;
      this.aliases = this.parseAliases(this.dataset.aliases);
      this.onChange = this.onChange.bind(this);

      document.addEventListener("simple-variant-picker:variantChange", this.onChange);
      window.addEventListener("variantChanged", this.onChange);
      document.addEventListener("change", this.onChange);
      this.sync();
    }

    disconnectedCallback() {
      document.removeEventListener("simple-variant-picker:variantChange", this.onChange);
      window.removeEventListener("variantChanged", this.onChange);
      document.removeEventListener("change", this.onChange);
      this.removeGiftInputs();
      this.initialized = false;
    }

    parseAliases(raw) {
      const aliases = new Map();
      String(raw || "").replace(/<br\s*\/?\s*>/gi, "\n").split("\n").forEach((line) => {
        const parts = line.split(/=|:/);
        if (parts.length >= 2) aliases.set(normalize(parts[0]), normalize(parts.slice(1).join("=")));
      });
      return aliases;
    }

    optionIndex(product, requestedName) {
      const names = (product.options || []).map((option) => typeof option === "string" ? option : option.name);
      const requested = normalize(requestedName);
      let index = names.findIndex((name) => normalize(name) === requested);
      if (index !== -1) return index;
      const commonNames = ["color", "colour", "renk"];
      index = names.findIndex((name) => commonNames.includes(normalize(name)));
      return index !== -1 ? index : 0;
    }

    selectedMainVariant(event) {
      const eventVariant = event?.detail?.variant || event?.detail;
      if (eventVariant && eventVariant.id && (eventVariant.options || eventVariant.option1)) return eventVariant;

      const idInput = this.form?.querySelector('input[name="id"], select[name="id"]');
      const variantId = idInput?.value || new URL(window.location.href).searchParams.get("variant");
      return this.mainProduct.variants.find((variant) => String(variant.id) === String(variantId))
        || this.mainProduct.variants.find((variant) => variant.available)
        || this.mainProduct.variants[0];
    }

    variantOptions(variant) {
      return variant.options || [variant.option1, variant.option2, variant.option3].filter((value) => value != null);
    }

    onChange(event) {
      if (event.target && this.contains(event.target)) return;
      window.requestAnimationFrame(() => this.sync(event));
    }

    sync(event) {
      this.form = document.getElementById(this.dataset.formId) || this.form;
      if (!this.form) {
        this.formRetryCount += 1;
        if (this.formRetryCount <= 30) window.setTimeout(() => this.sync(event), 100);
        else {
          this.hidden = false;
          this.status.textContent = "No se ha encontrado el formulario del producto.";
        }
        return;
      }
      this.formRetryCount = 0;

      const mainVariant = this.selectedMainVariant(event);
      if (!mainVariant) return this.setUnavailable();

      const mainIndex = this.optionIndex(this.mainProduct, this.dataset.mainOptionName);
      const giftIndex = this.optionIndex(this.giftProduct, this.dataset.giftOptionName);
      const mainValue = this.variantOptions(mainVariant)[mainIndex];
      const normalizedMain = normalize(mainValue);
      const desiredGiftValue = this.aliases.get(normalizedMain) || normalizedMain;

      const giftVariant = this.giftProduct.variants.find((variant) => {
        return normalize(this.variantOptions(variant)[giftIndex]) === desiredGiftValue;
      });

      if (!giftVariant) {
        const giftValues = [...new Set(this.giftProduct.variants.map((variant) => this.variantOptions(variant)[giftIndex]))]
          .filter(Boolean)
          .join(", ");
        return this.setUnavailable(`Ningún color del regalo coincide con «${mainValue}». Colores disponibles: ${giftValues || "ninguno"}.`);
      }
      if (giftVariant.available === false) {
        return this.setUnavailable(`El color de regalo «${mainValue}» está agotado.`);
      }
      this.setAvailable(giftVariant, giftIndex);
    }

    setAvailable(variant, giftIndex) {
      this.hidden = false;
      this.classList.remove("is-updating");
      this.status.textContent = "";
      this.optionValue.textContent = this.variantOptions(variant)[giftIndex] || variant.title;

      const image = variant.featured_image?.src
        || variant.featured_image
        || this.giftProduct.featured_image?.src
        || this.giftProduct.featured_image;
      if (image && this.image) this.image.src = String(image).replace(/(\.(?:jpe?g|png|webp))(\?.*)?$/i, "_180x$1$2");

      if (this.comparePrice) {
        const cents = Number(variant.price || variant.compare_at_price || 0);
        this.comparePrice.textContent = cents > 0 && window.Shopify?.formatMoney
          ? window.Shopify.formatMoney(cents)
          : this.comparePrice.textContent;
        this.comparePrice.hidden = cents <= 0;
      }

      this.injectGiftInputs(variant.id, this.mainProduct.id);
    }

    setUnavailable(message) {
      this.removeGiftInputs();
      const shouldHide = this.dataset.hideUnavailable !== "false";
      this.hidden = shouldHide;
      if (!shouldHide) {
        this.status.textContent = message || "No hay una variante de regalo disponible para este color.";
      }
    }

    injectGiftInputs(variantId, mainProductId) {
      this.removeGiftInputs();
      const existing = Array.from(this.form.querySelectorAll('input[name^="items["][name$="][id]"]'));
      const indexes = existing.map((input) => Number((input.name.match(/items\[(\d+)\]/) || [])[1])).filter(Number.isFinite);
      const index = indexes.length ? Math.max(...indexes) + 1 : 1;
      const container = document.createElement("div");
      container.className = "matching-variant-gift-inputs";
      container.dataset.matchingGiftOwner = this.dataset.blockId;
      container.innerHTML = `
        <input type="hidden" name="items[${index}][id]" value="${variantId}">
        <input type="hidden" name="items[${index}][quantity]" value="1">
        <input type="hidden" name="items[${index}][properties][_is_free_gift]" value="true">
        <input type="hidden" name="items[${index}][properties][_linked_to_product]" value="${mainProductId}">
      `;
      this.form.appendChild(container);
    }

    removeGiftInputs() {
      this.form?.querySelectorAll(".matching-variant-gift-inputs").forEach((element) => {
        if (element.dataset.matchingGiftOwner === this.dataset.blockId) element.remove();
      });
    }
  }

  customElements.define("matching-variant-gift", MatchingVariantGift);
})();
